/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Account Info MultiBalances History Modal
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.11.0
 */

import { useEffect, useState } from 'react';

import moment from 'moment';
import { useSelector } from 'react-redux';

import SimpleTable, { ISimpleTableColumns, ISimpleTableData } from '../../../components/SimpleTable/SimpleTable';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { Loader } from '../../../modules/loader/components/Loader';
import {
    ModalPopupContainerStyled,
    ModalPopupTextStyled,
    ModalPopupTitleStyled,
} from '../../../modules/modal/components/styled/ModalStyled';
import { formatBalance } from '../../../utils/formatText';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { accountInfoApi } from '../store';
import { AccountInfoMultiBalancesEventsBalanceStyled } from './styled/AccountInfoMultiBalancesStyled';

interface IAccountInfoMultiBalancesHistoryModalProps {
    expandedBalanceId: number;
    clientId: string;
}

const AccountInfoMultiBalancesHistoryModal = ({
                                                  clientId,
                                                  expandedBalanceId,
                                              }: IAccountInfoMultiBalancesHistoryModalProps) => {

    const translationAccountInfo = useSelector(makeSelectPageAccountInfoTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const [getMultiBalancesEvents, {
        data: multiBalanceEvents,
        isError: multiBalanceEventsError,
        isLoading: multiBalanceEventsIsLoading,
    }] = accountInfoApi.endpoints.getAccountInfoMultiBalanceEvents.useLazyQuery();

    const fieldsNames = translationAccountInfo.financialInfo.multiBalances.historyModalFields || {};

    const columns: ISimpleTableColumns[] = [{
        id: 'name',
        title: fieldsNames.name,
        flex: 1,
    }, {
        id: 'msisdn',
        title: fieldsNames.msisdn,
        width: 170,
    }, {
        id: 'balanceDate',
        title: fieldsNames.balanceDate,
        width: 160,
    }, {
        id: 'eventTypeName',
        title: fieldsNames.eventTypeName,
        width: 160,
    }, {
        id: 'productName',
        title: fieldsNames.productName,
        width: 160,
    }, {
        id: 'amount',
        title: fieldsNames.amount,
        width: 130,
    }];

    const [data, setData] = useState<ISimpleTableData[]>([]);

    useEffect(() => {
        getMultiBalancesEvents({ expandedBalanceId, clientId });
    }, [getMultiBalancesEvents, clientId, expandedBalanceId ]);

    useEffect(() => {
        if (!multiBalanceEvents?.multiBalanceEvents) return

        setData(multiBalanceEvents.multiBalanceEvents.map(item => ({
            amount: <AccountInfoMultiBalancesEventsBalanceStyled className={item.amount < 0 ? 'red' : ''}>
                {formatBalance(item.amount)} {translationCommon.currency}
            </AccountInfoMultiBalancesEventsBalanceStyled>,
            balanceDate: <div>
                {moment(item.balanceDate).format(EFormatData.FullData)}
                <br/>
                {moment(item.balanceDate).format(EFormatData.FullTime)}
            </div>,
            eventTypeName: item.eventTypeName,
            id: `${item.balanceDate}${item.eventTypeName}`,
            msisdn: item.msisdn,
            name: item.balanceActionTypeName,
            productName: item.productName,
        })));
    }, [multiBalanceEvents, translationCommon]);

    return <ModalPopupContainerStyled
        data-component={'modal-popup'}
        data-name={'account-info-multi-balances-history-modal'}
        width={'1107px'}>
        <ModalPopupTitleStyled data-name="modal-popup-title">
            {translationAccountInfo.financialInfo.multiBalances.historyModalTitle}
        </ModalPopupTitleStyled>
        <ModalPopupTextStyled data-name="modal-popup-text">
            {
                !multiBalanceEventsError
                    ? <Loader showLoader={multiBalanceEventsIsLoading}>
                        <SimpleTable bordered={true}
                                     columns={columns}
                                     data={data}
                                     dataTableName={'multiBalanceEvents'}
                                     maxHeight={'400px'}
                        />
                    </Loader>
                    : translationCommon.commonTable.error
            }
        </ModalPopupTextStyled>
    </ModalPopupContainerStyled>;
};

export default AccountInfoMultiBalancesHistoryModal;
